import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MIN_W = 280;
const MIN_H = 320;
const DEFAULT_W = 360;
const DEFAULT_H = 520;
const EDGE = 8;

/**
 * Floating panel for mission track playback.
 * Fixed to the viewport so it can be dragged anywhere on the page (including over the map).
 * Default placement is still near the right/sidebar; no modal backdrop so the map stays interactive.
 * Draggable (title bar) + resizable (corner handle).
 */
export default function MissionPlaybackFloatPanel({
  open = false,
  title = '任务轨迹回放',
  subtitle = '',
  onClose,
  children,
  empty = false,
  emptyText = '该任务暂无已记录轨迹的队员'
}) {
  const dragRef = useRef(null);
  const [size, setSize] = useState({ w: DEFAULT_W, h: DEFAULT_H });
  const [offset, setOffset] = useState({ x: 0, y: 48 });
  const [placed, setPlaced] = useState(false);

  const viewportSize = useCallback(() => ({
    vw: Math.max(320, window.innerWidth || document.documentElement.clientWidth || 1280),
    vh: Math.max(480, window.innerHeight || document.documentElement.clientHeight || 720)
  }), []);

  const clamp = useCallback((x, y, w, h) => {
    const { vw, vh } = viewportSize();
    const maxW = Math.max(MIN_W, vw - EDGE * 2);
    const maxH = Math.max(MIN_H, vh - EDGE * 2);
    const cw = Math.min(Math.max(MIN_W, w), maxW);
    const ch = Math.min(Math.max(MIN_H, h), maxH);
    const maxX = Math.max(EDGE, vw - cw - EDGE);
    const maxY = Math.max(EDGE, vh - ch - EDGE);
    return {
      x: Math.min(Math.max(EDGE, x), maxX),
      y: Math.min(Math.max(EDGE, y), maxY),
      w: cw,
      h: ch
    };
  }, [viewportSize]);

  useEffect(() => {
    if (!open) {
      setPlaced(false);
      return undefined;
    }
    // First open: pin near top-right of viewport (over member panel area by default)
    const id = requestAnimationFrame(() => {
      const { vw, vh } = viewportSize();
      const w = Math.min(DEFAULT_W, Math.max(MIN_W, vw - EDGE * 2));
      const h = Math.min(DEFAULT_H, Math.max(MIN_H, vh - EDGE * 2));
      const x = Math.max(EDGE, vw - w - 24);
      const y = 56;
      const c = clamp(x, y, w, h);
      setSize({ w: c.w, h: c.h });
      setOffset({ x: c.x, y: c.y });
      setPlaced(true);
    });
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !placed) return undefined;
    const onResize = () => {
      setOffset((prev) => {
        const c = clamp(prev.x, prev.y, size.w, size.h);
        setSize({ w: c.w, h: c.h });
        return { x: c.x, y: c.y };
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, placed, size.w, size.h, clamp]);

  const onDragStart = (e) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const orig = { ...offset };
    dragRef.current = { mode: 'move', startX, startY, orig };

    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d || d.mode !== 'move') return;
      const nx = orig.x + (ev.clientX - startX);
      const ny = orig.y + (ev.clientY - startY);
      const c = clamp(nx, ny, size.w, size.h);
      setOffset({ x: c.x, y: c.y });
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onResizeStart = (e) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const orig = { ...size, ...offset };
    dragRef.current = { mode: 'resize', startX, startY, orig };

    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d || d.mode !== 'resize') return;
      const nw = orig.w + (ev.clientX - startX);
      const nh = orig.h + (ev.clientY - startY);
      const c = clamp(orig.x, orig.y, nw, nh);
      setSize({ w: c.w, h: c.h });
      setOffset({ x: c.x, y: c.y });
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const style = useMemo(() => ({
    position: 'fixed',
    top: offset.y,
    left: offset.x,
    width: size.w,
    height: size.h,
    zIndex: 1200,
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
    border: '1px solid #90caf9',
    borderRadius: 12,
    boxShadow: '0 12px 32px rgba(13,71,161,0.22)',
    overflow: 'hidden',
    pointerEvents: 'auto'
  }), [offset, size]);

  if (!open || typeof document === 'undefined') return null;

  const body = !placed ? null : (
    <div style={style} role="dialog" aria-label={title}>
      <div
        onMouseDown={onDragStart}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 12px',
          background: 'linear-gradient(180deg, #e3f2fd 0%, #f5f9ff 100%)',
          borderBottom: '1px solid #c5d8f5',
          cursor: 'move',
          userSelect: 'none',
          flexShrink: 0
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0d47a1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
          </div>
          {subtitle ? (
            <div style={{ fontSize: 11, color: '#607d8b', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {subtitle}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          title="关闭回放面板"
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            border: 'none',
            background: 'transparent',
            color: '#546e7a',
            cursor: 'pointer',
            fontSize: 18,
            lineHeight: 1,
            fontWeight: 700,
            padding: '0 4px'
          }}
        >
          {'\u00d7'}
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 12 }}>
        {empty ? (
          <div style={{
            height: '100%',
            minHeight: 160,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: '#8a93a0',
            fontSize: 13,
            padding: 16,
            lineHeight: 1.6
          }}
          >
            {emptyText}
          </div>
        ) : children}
      </div>

      <div
        onMouseDown={onResizeStart}
        title="拖动调整大小"
        style={{
          position: 'absolute',
          right: 2,
          bottom: 2,
          width: 16,
          height: 16,
          cursor: 'nwse-resize',
          background: 'linear-gradient(135deg, transparent 50%, #90caf9 50%)',
          borderRadius: '0 0 10px 0',
          opacity: 0.9
        }}
      />
    </div>
  );

  return createPortal(body, document.body);
}
